import {IPavementStructureScenarioImperial} from "@/modules/structure/interfaces/pavementStructure";
import {BaseScenarioType} from "@/lib/types/IBaseScenario";
import {UnitSystem} from "@/interfaces/units";
import {
  IPerRoadLayerImperial,
  IPerRoadSeasonImperial,
  PerRoadLayerType,
  PrAxleConfigurationType
} from "@/modules/perRoad/interfaces/perRoad";
import {v4} from "uuid";
import {getBasicLayerSoil} from "@/modules/perRoad/lib/layers/createNewLayer";
import {acModulusByGrade, modulusByLayerType_psi} from "@/modules/perRoad/lib/layers/layerTypeDefaults";
import {DistributionTypes, IDistribution} from "@/lib/monteCarlo/interfaces";
import {poissonRatios} from "@/modules/perRoad/lib/poissonRatio";
import {axlePatterns} from "@/modules/perRoad/lib/simulation/axleLoadPatterns";
import {getUserId} from "@/modules/design/lib/r23ToSuiteTransformers";
import {IProjectMetadata} from "@/interfaces/report";


export const createNewPavementStructureScenario = (project: IProjectMetadata | null): IPavementStructureScenarioImperial => {
  const s: IPavementStructureScenarioImperial = {
    id: null,

    name: "New Pavement",
    user: getUserId(),
    project: project ? project.id : null,
    created: new Date(Date.now()).toISOString(),
    modified: new Date(Date.now()).toISOString(),
    visible: true,
    displayUnits: UnitSystem.Imperial,
    isComputedFromLegacy: false,

    description: "",

    scenarioType: BaseScenarioType.structure,
    unitSystem: UnitSystem.Imperial,
    axleType: PrAxleConfigurationType.TANDEM,

    loadPatternName: "Tandem Axle, Double Tire",
    responsePattern: "line",
    axleLoad_lb: 20000,

    numGridPoints: 2,
    numLinePoints: 50,


    selectedFatigueModel: "mndot",
    fatigueParamA: 2.83,
    fatigueParamB: 3.206,
    useFatigueEnduranceLimit: true,
    fatigueEnduranceLimit: -21,

    selectedRuttingModel: "ai",
    ruttingParamA: 1077000000000000000,
    ruttingParamB: 4.4843,

    loads: [
      ...axlePatterns[PrAxleConfigurationType.TANDEM].doubleTire
    ],

    structureSeasons: [
      season,
    ],

    structureLayers: [
      createNewBasicDeterministicLayerAc(),
      createNewBasicDeterministicLayerGranularBase(),
      getBasicLayerSoil(),
      getBasicLayerSoil(),
      // getBasicLayerSoil(),
    ],

    wesleaConfig: {
      drawId: v4(),
      criticallocations: [],
      layers: [],
      layerinterface: [1, 1, 1],
      loads: [],
      subgrademodulus: 150000,
      subgradepoissons: 0.35,
    },

    wesleaConfigs: [],
    wesleaResults: []
  };

  const l = [0, 1, 2, 3];
  l.forEach(i => {
    s.structureLayers[i].id = i;
  });

  return s;
};

const season: IPerRoadSeasonImperial = {
  id: null,
  name: "Default",
  duration_weeks: 26,
  enabled: true,
  temp_degF: 70,
  unitSystem: UnitSystem.Imperial,
};


/**
 * Default AC layer
 */
const getAcDefaultThicknessDeterministic = (): IDistribution => {
  return {
    id: -1,
    name: "Thickness (in)",
    max: null,
    mean: 5,
    min: null,
    mode: null,
    std: 0,
    location: null,
    shape: null,
    prob: DistributionTypes.deterministic,
  };
};

const getAcDefaultModulusDeterministic = (): IDistribution => {
  return {
    id: -1,
    name: "Modulus (psi)",
    max: null,
    mean: acModulusByGrade[70],
    min: null,
    mode: null,
    std: 0,
    location: null,
    shape: null,
    prob: DistributionTypes.deterministic,
  };
};

const createNewBasicDeterministicLayerAc = (): IPerRoadLayerImperial => {
  return {
    id: -1,
    name: "Asphalt Concrete",
    type: PerRoadLayerType.AC,
    unitSystem: UnitSystem.Imperial,
    enabled: true,
    poissonRatio: 0.35,
    pgGradeHigh: 70,
    pgGradeLow: -22,
    modulus_psi: [getAcDefaultModulusDeterministic()],
    thickness_in: getAcDefaultThicknessDeterministic(),
    performanceCriteria: [],
  };
};

/**
 * Default granular layer
 */

export const getDefaultModulusDeterministic = (layerType = PerRoadLayerType.GRANULAR_BASE): IDistribution => {
  return {
    id: null,
    name: "Modulus (psi)",
    max: null,
    mean: modulusByLayerType_psi[layerType],
    min: null,
    mode: null,
    std: 0,
    location: null,
    shape: null,
    prob: DistributionTypes.deterministic,
  };
};

const createNewBasicDeterministicLayerGranularBase = (): IPerRoadLayerImperial => {
  return {
    id: -1,
    name: "Granular Base",
    type: PerRoadLayerType.GRANULAR_BASE,
    unitSystem: UnitSystem.Imperial,
    enabled: true,
    poissonRatio: 0.35,
    pgGradeHigh: null,
    pgGradeLow: null,
    modulus_psi: [getDefaultModulusDeterministic()],
    thickness_in: getAcDefaultThicknessDeterministic(),
    performanceCriteria: [],
  };
};

/**
 * Generic layer creation
 */
const createNewGenericDeterministicLayer = (layerType: PerRoadLayerType,
                                            seasons: IPerRoadSeasonImperial[] = [],
                                            thicknessDistribution = getAcDefaultModulusDeterministic(),
                                            modulusDistribution = getDefaultModulusDeterministic()
): IPerRoadLayerImperial => {
  // This has season parameters so as to have the same function signature as the other new layer functions
  const newLayer: IPerRoadLayerImperial = createNewBasicDeterministicLayerAc();

  newLayer.name = `Default ${layerType}`;
  newLayer.type = layerType;
  newLayer.thickness_in = {...thicknessDistribution};

  // No seasons, so just one entry
  newLayer.modulus_psi = [getDefaultModulusDeterministic(newLayer.type)];

  console.log("Modulus dist", newLayer.modulus_psi);

  newLayer.performanceCriteria = [];
  newLayer.poissonRatio = poissonRatios[layerType].mean;

  return newLayer;
};


export const newPavementStructureLayerFunctionsByType = {
  // Custom factories
  [PerRoadLayerType.AC]: createNewBasicDeterministicLayerAc,
  [PerRoadLayerType.CRACKED_AC]: createNewBasicDeterministicLayerAc,
  [PerRoadLayerType.GRANULAR_BASE]: createNewBasicDeterministicLayerGranularBase,

  // Generic factory
  [PerRoadLayerType.ROCK]: createNewGenericDeterministicLayer,
  [PerRoadLayerType.SOIL]: createNewGenericDeterministicLayer,
  [PerRoadLayerType.BS_PCC]: createNewGenericDeterministicLayer,
  [PerRoadLayerType.CS_PCC]: createNewGenericDeterministicLayer,
  [PerRoadLayerType.RUBBLIZED_PCC]: createNewGenericDeterministicLayer,
  [PerRoadLayerType.PCC]: createNewGenericDeterministicLayer,
  [PerRoadLayerType.OTHER]: createNewGenericDeterministicLayer,
};
